import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { Check } from "lucide-react";
import { Container, Section, Eyebrow, DisplayHeading, BodyCopy, Button, Reveal, StatusBadge, Badge, TextLink } from "@/components/kit";
import { MaskedLinesInView } from "@/components/MaskedLines";
import Certificate from "@/components/Certificate";
import TrustScore, { ScoreBar } from "@/components/TrustScore";
import { CTASection } from "@/components/shared";
import { color } from "@/lib/theme";
import { fadeUp, stagger, viewport } from "@/lib/motion";

const DEMO_CERT = {
  certificate_id: "CERT-DEMO-000148", organization: "Demo Organization", ai_system: "Customer Support Agent",
  assurance_level: "Governed", status: "valid", issued_date: "2026-08-14", valid_until: "2027-08-14",
  assessment_type: "AI Security Assessment + Governance Review", system_type: "Customer-facing LLM agent",
  frameworks: ["ISO/IEC 42001", "NIST AI RMF", "OWASP LLM Top 10", "EU AI Act"],
  testing_engines: ["Prompt Injection", "Data Leakage", "Jailbreak Resistance", "Tool & Agent Abuse", "PII Handling"],
  test_coverage: [
    { category: "Prompt injection", result: "pass" },
    { category: "Sensitive data leakage", result: "pass" },
    { category: "Jailbreak resistance", result: "pass" },
    { category: "Tool & permission abuse", result: "review" },
    { category: "Output safety", result: "pass" },
  ],
  findings_summary: { critical: 0, high: 0, medium: 2, resolved: 3 },
  trust_score: 87, verification_hash: "a7f3c9e21b4d88f6e0c5a91d7b2e4f80c6a1d93e", sample: true,
};

/* ---------- Hero proof chain ---------- */
const CHAIN = [
  ["AI System", "Customer Service Agent · DEMO", "brand"],
  ["Assessment", "6 test categories", "brand"],
  ["Findings", "2 · mapped to controls", "warn"],
  ["Controls", "Mapped & owned", "brand-deep"],
  ["Evidence", "Signed · timestamped", "gold"],
  ["Assurance", "Governed", "gold"],
  ["Verified", "Current record", "valid"],
];
function ProofChain() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  return (
    <div ref={ref} className="relative mx-auto w-full max-w-[500px]">
      <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-brand/[0.04] blur-3xl" />
      <div className="pointer-events-none absolute -left-16 bottom-0 h-56 w-56 rounded-full bg-brand/[0.06] blur-3xl" />
      <div className="relative rounded-xl border border-line bg-panel-raised/70 p-5 backdrop-blur-xl sm:p-6">
        <div className="absolute inset-0 tech-grid opacity-60" />
        <div className="relative mb-4 flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-content-faint">Proof chain · demo</span>
          <span className="font-mono text-[8.5px] uppercase tracking-[0.18em] text-brand-soft/80">Illustrative</span>
        </div>
        <div className="relative flex flex-col">
          {CHAIN.map(([k, v, c], i) => (
            <motion.div key={k} initial={{ opacity: 0, x: -12 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ delay: 0.3 + i * 0.18, duration: 0.55, ease: [0.16, 1, 0.3, 1] }} className="relative flex items-center gap-4 py-2">
              <span className="relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border" style={{ borderColor: color(c, 0.4), background: color("ink") }}>
                <span className="h-2 w-2 rounded-full" style={{ background: color(c), boxShadow: `0 0 6px ${color(c, 0.35)}` }} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-content-faint">{k}</div>
                <div className="truncate text-sm text-content">{v}</div>
              </div>
              {i < CHAIN.length - 1 && <span className="absolute left-[11px] top-[34px] h-[calc(100%-20px)] w-px" style={{ background: color("line") }} />}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- Signature provenance trace ---------- */
const PROV = [
  { id: "system", label: "AI System", sub: "Customer Service Agent · DEMO", c: "brand", rel: null, note: "The demonstration AI system under assurance." },
  { id: "assessment", label: "Assessment", sub: "Prompt Injection Assessment", c: "brand", rel: "assessed by", note: "A dated assessment in a controlled environment." },
  { id: "finding", label: "Finding", sub: "Resolved · was HIGH", c: "warn", rel: "produced", note: "A finding, now remediated and evidenced." },
  { id: "control", label: "Control", sub: "AI-SEC-04", c: "brand-deep", rel: "addressed by", note: "The control that mitigates the finding." },
  { id: "evidence", label: "Evidence", sub: "EV-DEMO-00481 · signed", c: "gold", rel: "evidenced by", note: "Dated, recorded evidence linking every step." },
  { id: "assurance", label: "Assurance record", sub: "Governed", c: "gold", rel: "organized into", note: "The assurance state derived from the evidence." },
  { id: "verified", label: "Verified", sub: "Current", c: "valid", rel: "checked as", note: "Independently verifiable — no login needed." },
];
function ProvenanceTrace() {
  const [active, setActive] = useState("evidence");
  const n = PROV.find((x) => x.id === active);
  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_1fr] lg:items-start">
      <ol className="relative flex flex-col">
        <span className="absolute left-[11px] top-2 h-[calc(100%-1rem)] w-px bg-gradient-to-b from-brand/50 via-line to-valid/50" />
        {PROV.map((x, i) => {
          const isA = x.id === active;
          return (
            <li key={x.id} className="relative">
              {x.rel && <div className="ml-8 pb-1 pt-1"><span className="font-mono text-[9px] uppercase tracking-[0.18em] text-content-faint">{x.rel}</span></div>}
              <motion.button data-testid={`prov-node-${x.id}`} onMouseEnter={() => setActive(x.id)} onFocus={() => setActive(x.id)} onClick={() => setActive(x.id)} initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.6 }} transition={{ duration: 0.5, delay: i * 0.05 }} aria-label={`${x.label}: ${x.sub}`}
                className={`relative flex w-full items-center gap-4 rounded-md border px-4 py-3 text-left transition-colors duration-300 focus:outline-none ${isA ? "border-line-bright bg-panel-raised" : "border-line bg-panel-raised/70 hover:border-brand/50"}`}>
                <span className="relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border" style={{ borderColor: color(x.c, 0.4), background: color("ink") }}>
                  <span className="h-2 w-2 rounded-full" style={{ background: color(x.c), boxShadow: isA ? `0 0 10px ${color(x.c, 0.45)}` : `0 0 4px ${color(x.c, 0.3)}` }} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-mono text-[9px] uppercase tracking-[0.2em] text-content-faint">{x.label}</span>
                  <span className="block truncate text-sm text-content">{x.sub}</span>
                </span>
              </motion.button>
            </li>
          );
        })}
      </ol>
      <motion.div key={active} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="relative overflow-hidden rounded-lg border border-line bg-panel-raised p-7 lg:sticky lg:top-28">
        <span className="absolute left-0 top-0 h-full w-[3px]" style={{ background: color(n.c) }} />
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-content-faint">{n.label}</div>
        <h4 className="mt-2 font-display text-2xl font-medium text-content">{n.sub}</h4>
        <p className="mt-4 text-sm leading-relaxed text-content-muted">{n.note}</p>
        <p className="mt-5 font-mono text-[9px] uppercase tracking-[0.16em] text-content-faint">Demonstration data · every claim traces to evidence</p>
      </motion.div>
    </div>
  );
}

/* ---------- Verify mini flow ---------- */
const VSTEPS = ["Check record", "Scope confirmed", "Status current", "Verified"];
function VerifyFlow() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [step, setStep] = useState(-1);
  useEffect(() => {
    if (!inView) return;
    let i = -1; const t = setInterval(() => { i += 1; setStep(i); if (i >= VSTEPS.length - 1) clearInterval(t); }, 650);
    return () => clearInterval(t);
  }, [inView]);
  return (
    <div ref={ref} className="mt-5 flex flex-col gap-2.5">
      <div className="font-mono text-[10px] tracking-wide text-brand-soft">CERT-DEMO-000148</div>
      {VSTEPS.map((s, i) => {
        const done = i <= step; const last = i === VSTEPS.length - 1;
        return (
          <div key={s} className="flex items-center gap-3">
            <span className={`flex h-4 w-4 items-center justify-center rounded-full border transition-colors duration-300 ${done ? (last ? "border-valid bg-valid/20" : "border-brand-soft bg-brand/20") : "border-line-bright"}`}>
              {done && <Check size={10} className={last ? "text-valid" : "text-brand-soft"} strokeWidth={3} />}
            </span>
            <span className={`font-mono text-[11px] uppercase tracking-[0.14em] transition-colors duration-300 ${done ? (last ? "text-valid" : "text-content") : "text-content-faint"}`}>{s}</span>
          </div>
        );
      })}
    </div>
  );
}

const STAKEHOLDERS = [
  { id: "customer", label: "Customer", q: "Can I trust this AI?", hi: "Assurance state" },
  { id: "procurement", label: "Procurement", q: "What was actually assessed?", hi: "Assessment scope" },
  { id: "auditor", label: "Auditor", q: "Where is the evidence?", hi: "Evidence records" },
  { id: "board", label: "Board", q: "What is our current assurance state?", hi: "Status · current" },
  { id: "regulator", label: "Regulator", q: "What is the scope and record?", hi: "Verification record" },
];

const VMEANS = [
  ["Identity", "The assurance record exists in Meira."],
  ["Scope", "It shows which AI system and assessment it covers."],
  ["Status", "You can tell whether the record is current."],
  ["Evidence", "It traces back to the evidence captured during assessment."],
];

export default function Assurance() {
  const [stake, setStake] = useState("auditor");
  const sh = STAKEHOLDERS.find((s) => s.id === stake);
  return (
    <>
      {/* 01 HERO */}
      <section className="relative overflow-hidden pb-12 pt-28 sm:pb-16 sm:pt-36 md:pb-24 md:pt-44">
        <div className="pointer-events-none absolute inset-0 tech-grid radial-fade opacity-50" />
        <div className="pointer-events-none absolute -top-20 left-1/3 h-[280px] w-[420px] -translate-x-1/2 rounded-full bg-brand/[0.04] blur-[95px] sm:-top-32 sm:h-[460px] sm:w-[820px] sm:blur-[130px]" />
        <Container className="relative">
          <div className="grid items-center [&>*]:min-w-0 gap-10 sm:gap-14 lg:grid-cols-[1.05fr_0.95fr]">
            <div>
              <motion.div variants={fadeUp} initial="hidden" animate="show"><Eyebrow tone="brand">Assurance &amp; Proof</Eyebrow></motion.div>
              <h1 className="mt-6 font-display text-[40px] font-medium leading-[1.02] tracking-tight text-content sm:mt-7 sm:text-6xl md:text-[72px]">
                <MaskedLinesInView lines={["Proof,"]} />
                <span className="block text-gradient brand-glow"><MaskedLinesInView lines={["not promises."]} /></span>
              </h1>
              <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3}>
                <BodyCopy className="mt-7 max-w-xl">Turn AI security and governance outcomes into assurance that others can independently verify.</BodyCopy>
                <div className="mt-8 flex max-w-xs flex-col items-stretch gap-3 sm:max-w-none sm:flex-row sm:flex-wrap sm:gap-4">
                  <Button to="/verify" variant="primary" data-testid="assurance-verify-hero" className="uppercase tracking-[0.12em]">See How Verification Works</Button>
                  <Button to="/verify" variant="outline">Verify a Certificate</Button>
                </div>
              </motion.div>
            </div>
            <ProofChain />
          </div>
        </Container>
      </section>

      {/* 02 ASSURANCE LADDER */}
      <Section className="border-t border-line bg-panel-raised/30">
        <Container>
          <Reveal><Eyebrow>Assurance model</Eyebrow></Reveal>
          <DisplayHeading className="mt-6 max-w-2xl text-3xl leading-tight sm:text-4xl">From tested to trusted.</DisplayHeading>
          <Reveal i={1}><BodyCopy className="mt-6 max-w-2xl">Meira expresses assurance in clear, scoped states so stakeholders understand what has actually been assessed.</BodyCopy></Reveal>
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewport} className="mt-8 grid items-stretch gap-3 sm:mt-12 md:grid-cols-[1fr_auto_1fr_auto_1fr]">
            {[
              { n: "01", t: "Tested", tone: "brand", d: "Security assessment completed. Findings documented and evidence captured." },
              { n: "02", t: "Governed", tone: "brand", d: "Testing connected to ownership, risks, controls, policies and evidence." },
              { n: "03", t: "Assured", tone: "gold", d: "Defined assessment scope completed and represented as a verifiable Meira assurance record." },
            ].map((s, i, arr) => (
              <React.Fragment key={s.n}>
                <motion.div variants={fadeUp} className={`flex h-full flex-col rounded-lg border p-5 sm:p-7 ${s.tone === "gold" ? "border-gold/30 bg-gradient-to-b from-gold/8 to-transparent" : "border-brand/25 bg-brand/5"}`}>
                  <span className={`font-mono text-[11px] ${s.tone === "gold" ? "text-gold" : "text-brand-soft"}`}>{s.n}</span>
                  <h3 className={`mt-3 font-display text-2xl font-medium ${s.tone === "gold" ? "text-gradient-gold" : "text-content"}`}>{s.t}</h3>
                  <p className="mt-3 text-[14px] leading-relaxed text-content-muted">{s.d}</p>
                </motion.div>
                {i < arr.length - 1 && <div className="flex items-center justify-center py-1 md:py-0"><span className="text-content-faint"><svg width="26" height="12" viewBox="0 0 26 12" fill="none" className="rotate-90 md:rotate-0"><path d="M0 6h22m0 0l-5-5m5 5l-5 5" stroke="currentColor" strokeWidth="1" /></svg></span></div>}
              </React.Fragment>
            ))}
          </motion.div>
          <Reveal i={1}><p className="mt-6 max-w-2xl text-[13px] leading-relaxed text-content-muted">Beyond assured, Meira also screens framework readiness (ISO/IEC 42001, EU AI Act, NIST AI RMF) — readiness is not certification.</p></Reveal>
        </Container>
      </Section>

      {/* 03 TRUST PASSPORT */}
      <Section className="border-t border-line">
        <Container>
          <div className="grid gap-10 sm:gap-14 lg:grid-cols-[1fr_1fr] lg:items-center">
            <div>
              <Reveal><Eyebrow tone="brand">Meira Trust Passport</Eyebrow></Reveal>
              <DisplayHeading className="mt-6 text-3xl leading-tight sm:text-4xl">One AI system. One living assurance identity.</DisplayHeading>
              <Reveal i={1}><BodyCopy className="mt-6">The Trust Passport brings together an AI system’s assurance state, security results, governance coverage, framework readiness, findings and verification history.</BodyCopy></Reveal>
            </div>
            <Reveal i={1}>
              <div className="relative overflow-hidden rounded-lg border border-line bg-panel-raised p-5 sm:p-7">
                <div className="pointer-events-none absolute -right-16 -top-16 h-52 w-52 rounded-full bg-brand/[0.04] blur-3xl" />
                <div className="flex flex-col items-start gap-5 border-b border-line pb-5 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-content-faint">Meira Trust Passport</div>
                    <h4 className="mt-2 font-display text-xl text-content">Customer Service Agent</h4>
                    <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.16em] text-brand-soft/80">Demonstration system</div>
                    <div className="mt-3"><StatusBadge status="active">Governed</StatusBadge></div>
                  </div>
                  <div className="mx-auto sm:mx-0">
                    <TrustScore value={87} size={110} label="Trust Score · illustrative" />
                  </div>
                </div>
                <div className="mt-6 flex flex-col gap-4">
                  <ScoreBar label="Security coverage" value={92} tone="valid" />
                  <ScoreBar label="Governance coverage" value={84} tone="brand" delay={0.1} />
                  <ScoreBar label="Framework readiness" value={78} tone="gold" delay={0.2} />
                </div>
                <div className="mt-6 grid grid-cols-1 gap-px overflow-hidden rounded-md border border-line bg-panel-raised sm:grid-cols-3">
                  {[["Open findings", "2"], ["Evidence records", "24 demo"], ["Certificate", "Verified"]].map(([k, v]) => (
                    <div key={k} className="bg-ink p-3"><div className="font-mono text-[9px] uppercase tracking-wide text-content-faint">{k}</div><div className="mt-1 text-[13px] text-content">{v}</div></div>
                  ))}
                </div>
                <p className="mt-4 font-mono text-[9px] leading-relaxed tracking-wide text-content-faint">Demonstration data. Trust Score shown for interface illustration — not a claim of scientific certainty.</p>
              </div>
            </Reveal>
          </div>
        </Container>
      </Section>

      {/* 04 SIGNATURE PROVENANCE */}
      <Section className="relative overflow-hidden border-t border-line bg-panel-raised/50">
        <div className="pointer-events-none absolute -right-40 top-1/4 hidden h-[520px] w-[520px] rounded-full bg-brand/[0.04] blur-[130px] sm:block" />
        <div className="pointer-events-none absolute -left-40 top-0 hidden h-[420px] w-[420px] rounded-full bg-brand/[0.05] blur-[130px] sm:block" />
        <div className="pointer-events-none absolute inset-0 tech-grid opacity-30" />
        <Container className="relative">
          <Reveal><Eyebrow tone="brand">From evidence to proof</Eyebrow></Reveal>
          <DisplayHeading className="mt-6 max-w-3xl text-3xl leading-tight sm:text-4xl md:text-5xl">Every assurance claim should lead somewhere.</DisplayHeading>
          <Reveal i={1}><BodyCopy className="mt-6 max-w-2xl">Meira connects what was tested to what was found, what controls apply, what evidence exists and what can ultimately be verified.</BodyCopy></Reveal>
          <Reveal i={1} className="mt-10 sm:mt-14"><ProvenanceTrace /></Reveal>
        </Container>
      </Section>

      {/* 05 CERTIFICATE + VERIFICATION */}
      <Section className="border-t border-line">
        <Container>
          <div className="grid gap-10 sm:gap-14 lg:grid-cols-[1fr_1fr] lg:items-center">
            <Reveal><Certificate cert={DEMO_CERT} /></Reveal>
            <div>
              <Reveal><Eyebrow tone="brand">Public verification</Eyebrow></Reveal>
              <DisplayHeading className="mt-6 text-3xl leading-tight sm:text-4xl">Verify in seconds.</DisplayHeading>
              <Reveal i={1}>
                <BodyCopy className="mt-6">Check the certificate ID to see what was assessed, its scope, status and verification record — without a Meira account.</BodyCopy>
                <div className="mt-6 rounded-lg border border-line bg-panel-raised/60 p-5 sm:p-6"><VerifyFlow /></div>
                <p className="mt-4 font-mono text-[11px] leading-relaxed tracking-wide text-content-muted">This is a Meira AI assurance record, not an ISO/IEC 42001 certificate. Formal ISO/IEC certification is issued independently by an accredited certification body.</p>
                <div className="mt-6"><Button to="/verify" variant="primary" data-testid="assurance-verify" className="uppercase tracking-[0.12em]">Verify a Certificate</Button></div>
              </Reveal>
            </div>
          </div>
        </Container>
      </Section>

      {/* 06 WHAT VERIFIED MEANS */}
      <Section className="border-t border-line bg-panel-raised/30">
        <Container>
          <Reveal><Eyebrow>Transparency</Eyebrow></Reveal>
          <DisplayHeading className="mt-6 max-w-2xl text-3xl leading-tight sm:text-4xl">Verified has a precise meaning.</DisplayHeading>
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewport} className="mt-8 grid gap-px overflow-hidden rounded-lg border border-line bg-panel-raised sm:mt-12 sm:grid-cols-2 lg:grid-cols-4">
            {VMEANS.map(([t, d]) => (
              <motion.div key={t} variants={fadeUp} className="flex flex-col gap-2 bg-panel-raised p-6">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-valid">{t}</span>
                <p className="text-[14px] leading-relaxed text-content-muted">{d}</p>
              </motion.div>
            ))}
          </motion.div>
          <Reveal i={1}><p className="mt-6 max-w-3xl text-[14px] leading-relaxed text-content-muted"><span className="text-content">Verified does not mean risk-free, universally compliant, or formally ISO-certified.</span> It means the displayed Meira assurance record can be independently checked against its recorded scope and status.</p></Reveal>
        </Container>
      </Section>

      {/* 07 MEIRA VS CERTIFICATION */}
      <Section className="border-t border-line">
        <Container>
          <Reveal><Eyebrow>Independence</Eyebrow></Reveal>
          <DisplayHeading className="mt-6 max-w-3xl text-3xl leading-tight sm:text-4xl">Meira assurance and formal certification are different — by design.</DisplayHeading>
          <div className="mt-8 grid gap-4 sm:mt-12 sm:gap-5 md:grid-cols-2">
            <Reveal>
              <div className="h-full rounded-lg border border-brand/25 bg-brand/5 p-5 sm:p-8">
                <Badge tone="brand">Meira Assurance</Badge>
                <dl className="mt-5 flex flex-col gap-3 text-[14px]">
                  {[["Purpose", "Evidence-backed assurance of a defined AI assessment."], ["Produced by", "Meira AI."], ["Shows", "Scope · assessment · evidence · findings · assurance state · verification record."], ["Can be checked", "Yes — through Meira verification."]].map(([k, v]) => (
                    <div key={k} className="flex flex-col gap-1 sm:flex-row sm:gap-3"><dt className="font-mono text-[10px] uppercase tracking-[0.14em] text-content-faint sm:w-28 sm:shrink-0">{k}</dt><dd className="text-content-muted">{v}</dd></div>
                  ))}
                </dl>
              </div>
            </Reveal>
            <Reveal i={1}>
              <div className="h-full rounded-lg border border-gold/25 bg-gold/5 p-5 sm:p-8">
                <Badge tone="gold">Formal ISO/IEC 42001 Certification</Badge>
                <dl className="mt-5 flex flex-col gap-3 text-[14px]">
                  {[["Purpose", "Formal certification of an AI management system against ISO/IEC 42001."], ["Issued by", "An independent accredited certification body."], ["Meira's role", "Help organize governance, controls, evidence and readiness for the process."], ["Result", "A formal certificate — issued independently, not by Meira."]].map(([k, v]) => (
                    <div key={k} className="flex flex-col gap-1 sm:flex-row sm:gap-3"><dt className="font-mono text-[10px] uppercase tracking-[0.14em] text-content-faint sm:w-28 sm:shrink-0">{k}</dt><dd className="text-content-muted">{v}</dd></div>
                  ))}
                </dl>
              </div>
            </Reveal>
          </div>
          <Reveal i={1}><p className="mt-6 font-mono text-[12px] tracking-wide text-brand">Meira does not issue ISO/IEC 42001 certification.</p></Reveal>
        </Container>
      </Section>

      {/* 08 STAKEHOLDER STRIP */}
      <Section className="border-t border-line bg-panel-raised/30">
        <Container>
          <Reveal><Eyebrow tone="brand">One record. Different questions.</Eyebrow></Reveal>
          <DisplayHeading className="mt-6 max-w-2xl text-3xl leading-tight sm:text-4xl">A shared trust object — not another internal dashboard.</DisplayHeading>
          <div className="mt-8 grid gap-6 sm:mt-12 sm:gap-8 lg:grid-cols-[1fr_1fr] lg:items-center">
            <div className="flex flex-wrap gap-2.5 sm:gap-3 lg:flex-col lg:items-start">
              {STAKEHOLDERS.map((s) => (
                <button key={s.id} data-testid={`stakeholder-${s.id}`} onMouseEnter={() => setStake(s.id)} onFocus={() => setStake(s.id)} onClick={() => setStake(s.id)}
                  className={`inline-flex min-h-[40px] items-center gap-2.5 rounded-full border px-4 py-2 text-left text-[13px] font-medium transition-colors duration-300 focus:outline-none sm:gap-3 sm:px-5 sm:py-2.5 sm:text-sm ${stake === s.id ? "border-brand/50 bg-brand/10 text-content" : "border-line text-content-muted hover:border-brand/50 hover:text-content"}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${stake === s.id ? "bg-brand" : "bg-line-bright"}`} />{s.label}
                </button>
              ))}
            </div>
            <div className="relative min-h-[180px] overflow-hidden rounded-lg border border-line bg-panel-raised p-6 sm:min-h-[200px] sm:p-9">
              <div className="pointer-events-none absolute -right-16 -top-16 h-52 w-52 rounded-full bg-brand/[0.04] blur-3xl" />
              <AnimatePresence mode="wait">
                <motion.div key={sh.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.4 }}>
                  <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-content-faint">{sh.label}</div>
                  <h3 className="mt-3 font-display text-[22px] font-medium leading-snug text-content sm:mt-4 sm:text-3xl">“{sh.q}”</h3>
                  <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-gold/25 bg-gold/8 px-3 py-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                    <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-gold">Passport · {sh.hi}</span>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </Container>
      </Section>

      <CTASection eyebrow="Prove it" title={["Trust should", "be verifiable."]} body="Turn AI security and governance into evidence your customers, partners and assurance stakeholders can independently check." />
    </>
  );
}
